// Copyright (c) Ping. All rights reserved.
//
// Web host for the PEER side of no-QR recovery peer-history
// (`ensureRecoveredPeerDevicesAdmitted` from @ping/ui). Wires the shared,
// platform-agnostic routine to the web BE client (device discovery, KP claim,
// sealed history transfer) + the live SDK client/transport, and provides the
// LOCAL persisted admitted-set (settings_kv) that prevents duplicate MLS leaves.
//
// Triggered conservatively from WebChatRoom on conversation open and on app
// foreground (see WebChatRoom). Rate-limited per app session per conversation
// here unless the advertising-device set changed.

import {
  bytesToUrlB64NoPad,
  ensureRecoveredPeerDevicesAdmitted,
  hexToBytes,
  urlB64NoPadToBytes,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { log } from '@core/log/log';
import { getDeviceIdBytes } from '@core/network/deviceKeys';
import {
  claimAdmissionKeyPackages,
  type DeviceWithDeliveryKey,
  fetchConversationMemberAccountIds,
  listDevicesWithDeliveryKey,
} from '@core/network/users';
import { db } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { sendLinkingHistoryToDevice } from './sendLinkingHistory';

const ADMITTED_KEY = '__recovery.peerAdmitted.v1';

interface AdmittedSet {
  keys: string[];
}

async function loadAdmittedSet(): Promise<Set<string>> {
  try {
    const rec = await db.settings_kv.get(ADMITTED_KEY);
    const v = rec?.value as AdmittedSet | undefined;
    return new Set(Array.isArray(v?.keys) ? v!.keys : []);
  } catch {
    return new Set();
  }
}

async function recordAdmittedKey(key: string): Promise<void> {
  try {
    const set = await loadAdmittedSet();
    if (set.has(key)) return;
    set.add(key);
    await db.settings_kv.put({
      key: ADMITTED_KEY,
      value: { keys: Array.from(set) } satisfies AdmittedSet,
      updatedAt: Date.now(),
    });
  } catch {
    /* best-effort */
  }
}

// Session-scoped rate limit: a fingerprint of the advertising-device set we last
// processed per conversation. We re-run only when that set changes (a new
// recovered device advertised) — so conversation re-opens / foregrounds within a
// session don't re-sweep the BE on every tick.
const lastRunFingerprint = new Map<string, string>();

function fingerprint(devices: readonly DeviceWithDeliveryKey[]): string {
  return devices
    .map((d) => d.deviceId)
    .sort()
    .join(',');
}

interface AdmitClientLike {
  members(id: Uint8Array): Promise<ReadonlyArray<{ user_id: Uint8Array; leaf_index: number }>>;
  getConversation(id: Uint8Array): {
    addMembers(entries: Array<{ deviceId: Uint8Array; keyPackage: Uint8Array }>): Promise<void>;
  };
}

/**
 * Admit + history-seed any recovered peer devices in `conversationIdHex`.
 * Idempotent, best-effort, rate-limited per app session unless the advertising
 * device set changed. Safe to call on conversation open + app foreground.
 */
export async function ensureRecoveredPeerDevicesAdmittedForConversation(
  conversationIdHex: string,
): Promise<void> {
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') return;
  const client = session.status.client as unknown as AdmitClientLike;
  if (typeof client.members !== 'function' || typeof client.getConversation !== 'function') {
    return;
  }

  const identity = await retrieveIdentity();
  if (identity === null) return;
  const selfAccountId = identity.accountId;

  // Self device id bytes — informational on the history bundle; resolved once.
  let selfDeviceIdBytes: Uint8Array;
  try {
    selfDeviceIdBytes = await getDeviceIdBytes();
  } catch {
    return;
  }

  // Cheap pre-check for the rate limit: gather advertising devices across the
  // conversation's member accounts, fingerprint, and bail if unchanged this
  // session. (The shared routine re-fetches per account, but the fingerprint
  // keeps the common no-op case from running the full admit sweep.)
  let allAdvertised: DeviceWithDeliveryKey[];
  try {
    // Member ACCOUNT ids must come from the server roster (16-byte directory
    // account ids), NOT the MLS roster `user_id` (32-byte SHA256(pubkey), a
    // different id space) — feeding the latter to `/v1/devices/{id}` 400s.
    const accountIds = (await fetchConversationMemberAccountIds(conversationIdHex)).filter(
      (a) => a.length > 0 && a !== selfAccountId,
    );
    if (accountIds.length === 0) return;
    const lists = await Promise.all(
      accountIds.map((a) =>
        listDevicesWithDeliveryKey(a).catch(() => [] as readonly DeviceWithDeliveryKey[]),
      ),
    );
    allAdvertised = lists.flat();
  } catch {
    return;
  }
  if (allAdvertised.length === 0) return;
  const fp = fingerprint(allAdvertised);
  if (lastRunFingerprint.get(conversationIdHex) === fp) return;
  lastRunFingerprint.set(conversationIdHex, fp);

  const admittedSet = await loadAdmittedSet();

  await ensureRecoveredPeerDevicesAdmitted({
    conversationIdHex,
    client,
    transport: getCurrentTransport(),
    selfAccountId,
    listDevicesWithDeliveryKey,
    claimKeyPackageForDevice: async ({ deviceIdB64Url }) => {
      // Atomically CLAIM one KeyPackage for the peer's device (consuming, with
      // last-resort fallback) — device-scoped, so it works cross-account. This
      // replaces the legacy non-consuming `GET /v1/devices/{id}` LIST, where two
      // admitters could hand the SAME KeyPackage to the peer for two chats and
      // the second Welcome would be dropped ("No matching key package").
      const kps = await claimAdmissionKeyPackages({ deviceIdBase64Url: deviceIdB64Url, count: 1 });
      return kps[0] ?? null;
    },
    sendHistoryToDevice: async ({ recipientDeviceIdB64Url, deliveryPubkeyB64Url, conversationIdHex: convHex }) => {
      await sendLinkingHistoryToDevice({
        recipientDeviceIdB64Url,
        recipientPub: urlB64NoPadToBytes(deliveryPubkeyB64Url),
        selfDeviceIdBytes,
        conversationIds: [hexToBytes(convHex)],
      });
    },
    hasAdmitted: async (key) => admittedSet.has(key),
    recordAdmitted: async (key) => {
      admittedSet.add(key);
      await recordAdmittedKey(key);
    },
    b64UrlToBytes: urlB64NoPadToBytes,
    hexToBytes,
    userIdToAccountId: bytesToUrlB64NoPad,
    log,
  });
}

/**
 * IMMEDIATE re-admit of a re-onboarding peer ACCOUNT across EVERY conversation we
 * share with it. Triggered by the backend `recovery_readmit` account-event (the
 * peer just advertised a fresh-leaf delivery key after a full-wipe sign-out /
 * recovery), so it is re-added to all shared chats + shipped the history it missed
 * right away — instead of only when we next happen to open each chat.
 *
 * `accountIdB64Url` is the peer's 16-byte directory account id (base64url), as
 * carried on the push. Best-effort + idempotent: it reuses the per-conversation
 * routine (whose per-session fingerprint re-runs precisely because the peer's
 * advertising-device set just changed) and skips chats we've deleted or left
 * (we can't admit anyone to a group we're no longer a member of).
 */
export async function ensureRecoveredPeerAccountAdmittedEverywhere(
  accountIdB64Url: string,
): Promise<void> {
  const acct = accountIdB64Url.trim();
  if (acct.length === 0) return;
  let rows: Array<{
    id: string;
    peerAccountId?: string;
    memberAccountIds?: string[];
    deletedAt?: number;
    leftAt?: number;
  }>;
  try {
    rows = await db.conversations.toArray();
  } catch {
    return;
  }
  const shared = rows.filter(
    (c) =>
      (c.deletedAt ?? 0) === 0 &&
      (c.leftAt ?? 0) === 0 &&
      (c.peerAccountId === acct || (c.memberAccountIds?.includes(acct) ?? false)),
  );
  if (shared.length === 0) return;
  log.info({ event: 'recovery.peerAdmit.readmitAccount', conversations: shared.length });
  for (const c of shared) {
    await ensureRecoveredPeerDevicesAdmittedForConversation(c.id).catch(() => undefined);
  }
}
